import type { ReactNode } from 'react'

import type { Tendencia } from './cifras'
import estilos from './CifrasDelPeriodo.module.css'

/**
 * UNA CIFRA DEL PERIODO, EN EL PANEL MORADO.
 *
 * Johan: «le falta a La semana unas cards moradas como el dónde está el
 * equipo». Es el mismo lenguaje de ese panel: tarjeta translúcida, ícono en
 * un círculo de su color, rótulo en versalitas, la cifra grande en su color
 * y una barra fina debajo. Aquí la barra son los últimos periodos —la de
 * ahora entera, las de antes tenues—, y junto a la cifra va el aumento.
 *
 * El color dice qué es (oro lo finalizado, verde lo aceptado, rojo lo
 * rechazado, azul lo cotizado); el del aumento dice si es bueno o malo.
 */
export type Tono = 'oro' | 'verde' | 'rojo' | 'azul'

type Props = {
  tono: Tono
  icono: ReactNode
  rotulo: string
  valor: string
  detalle?: ReactNode
  /** «+200 %», «−50 %», «nuevo», «igual». */
  aumento: string
  tendencia: Tendencia
  /** En rechazadas subir es malo: invierte el color. */
  subirEsMalo?: boolean
  /** Los últimos periodos, del más viejo a este. */
  serie: number[]
  /** «vs. 21 – 24 sept: 3 · $3,08 M». */
  comparacion: ReactNode
}

export function TarjetaDeCifra({
  tono,
  icono,
  rotulo,
  valor,
  detalle,
  aumento,
  tendencia,
  subirEsMalo = false,
  serie,
  comparacion,
}: Props) {
  const bueno = tendencia === 'igual' ? null : (tendencia === 'sube') !== subirEsMalo
  const maximo = Math.max(1, ...serie)
  const enCero = valor === '0'

  return (
    <div className={`${estilos.celda} ${estilos[tono]}`}>
      <p className={estilos.celdaTope}>
        <span className={estilos.celdaIcono} aria-hidden="true">
          {icono}
        </span>
        {rotulo}
      </p>

      <p className={estilos.celdaCifra}>
        <span className={`${estilos.celdaValor} ${enCero ? estilos.celdaCero : ''}`}>{valor}</span>
        <span
          className={`${estilos.aumento} ${
            bueno === null
              ? estilos.aumentoIgual
              : bueno
              ? estilos.aumentoBueno
              : estilos.aumentoMalo
          }`}
          title="Contra el periodo anterior, a la misma altura"
        >
          {tendencia === 'sube' ? '▲ ' : tendencia === 'baja' ? '▼ ' : ''}
          {aumento}
        </span>
      </p>

      {detalle && <p className={estilos.celdaDetalle}>{detalle}</p>}

      {/* Los últimos periodos: la de ahora en su color, las de antes tenues. */}
      <div className={estilos.celdaBarras} aria-hidden="true">
        {serie.map((v, i) => (
          <span
            key={i}
            className={i === serie.length - 1 ? estilos.celdaBarraActual : estilos.celdaBarra}
            style={{ height: `${Math.max(v > 0 ? 14 : 6, (v / maximo) * 100)}%` }}
          />
        ))}
      </div>

      <p className={estilos.celdaComparacion}>{comparacion}</p>
    </div>
  )
}
